import { Loader } from "./Loader";
import { LoaderEvent } from "../events/LoaderEvent";

export abstract class BaseLoader extends Loader {
    static STATE_IDLE = 0;
    static STATE_LOADING = 1;
    static STATE_LOADED = 2;
    static STATE_ERROR = 4;

    protected _state = BaseLoader.STATE_IDLE;
    protected _lastProgress = 0;
    protected _src: string;
    protected _id: string;
    protected _loadingRequested = false;
    protected _request: any = null;
    protected _cacheTimestamp: string;
    protected _prependURL: string;
    protected _getParameters: string;
    protected _requestBody: any;

    // TODO: implement getData in all loaders and return null if the content is not loaded
    // TODO: implement unloading of all loaders (if possible)
    constructor(src: string, cacheTimestamp?: string, prependURL?: string, id?: string, body?: any) {
        super();

        if (cacheTimestamp == undefined)
            cacheTimestamp = Loader.cacheTimestamp;
        this._cacheTimestamp = cacheTimestamp;

        if (prependURL == undefined)
            prependURL = Loader.prependURL;
        this._prependURL = prependURL;

        this._requestBody = body
        
        if (!body) {
            this.constructGetParameters();
        }

        if (id == undefined)
            id = src;
        this._id = id;

        Loader.addLoader(this);

        this._src = src;

        this.setUpRequest();
    }

    abstract getData(): any;

    get src() {
        return this._src;
    }

    isIdle() {
        return this._state == BaseLoader.STATE_IDLE;
    }

    isLoading() {
        return this._state == BaseLoader.STATE_LOADING;
    }

    isLoaded() {
        return this._state == BaseLoader.STATE_LOADED;
    }

    hasError() {
        return this._state == BaseLoader.STATE_ERROR;
    }

    load() {
        if (this._state == BaseLoader.STATE_LOADING)
            return;
        else if (this._state == BaseLoader.STATE_LOADED) {
            var self = this;
            // report the progress and call _onLoaded asynchronously
            setTimeout(function () {
                self.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS,
                    { progress: 1, progressChange: 0 }));
                self.onLoaded();
            }, 0);
            return;
        }
        else if (this._state == BaseLoader.STATE_ERROR ||
            this._request.readyState == XMLHttpRequest.UNSENT) {
            this.openRequest();
        }

        this._lastProgress = 0;
        this._state = BaseLoader.STATE_LOADING;

        if (this._request.readyState == XMLHttpRequest.OPENED) {
            if (this._requestBody) {
                this._request.send(JSON.stringify(this._requestBody));
                this._requestBody = null;
            } else {
                this._request.send();
            }
        } else {
            this._loadingRequested = true;
        }
    }

    stop() {
        if (this._state != BaseLoader.STATE_LOADING)
            return;

        this._state = BaseLoader.STATE_IDLE;

        if (this._request)
            this._request.abort();
    }

    dispose() {
        if (this._request) {
            this._request.abort();
            this._request.onreadystatechange = null;
            this._request.onprogress = null;
            this._request = null;
            this._requestBody = null;
        }

        Loader.removeLoader(this);

        super.dispose();
    }

    protected abstract getResponseType(): string;

    protected abstract onLoaded(event?: any): void;

    protected abstract onError(event: any, param1?, param2?): void;

    protected setUpRequest() {
        this._request = new XMLHttpRequest();

        if (this._requestBody) {
            this.openPOSTRequest();
            this.setRequestHeader("Content-Type", "application/json");
        } else {
            this.openRequest();
        }
        this._request.responseType = this.getResponseType();

        this._request.onreadystatechange = this.onXHRReadyStateChange.bind(this);
        this._request.onprogress = this.onXHRProgress.bind(this);
    }

    private openRequest() {
        const prependURL = this._prependURL ? this._prependURL : "";

        this._request.open('GET', prependURL + this._src + this._getParameters);
    }

    private openPOSTRequest() {
        const prependURL = this._prependURL ? this._prependURL : "";

        this._request.open('POST', prependURL + this._src);
    }

    private setRequestHeader(header = null, value = null) {
        if (header)
            this._request.setRequestHeader(header, value);
    }

    private constructGetParameters() {
        this._getParameters = this._cacheTimestamp ? "?v=" + this._cacheTimestamp : "";
    }

    private onXHRReadyStateChange(event: any) {
        if (this._request.readyState == XMLHttpRequest.OPENED) {
            if (this._loadingRequested) {
                this._request.send();
                this._loadingRequested = false;
            }
        }
        else if (this._request.readyState == XMLHttpRequest.DONE) {
            if (this._request.status >= 200 && this._request.status < 400) {
                this._state = BaseLoader.STATE_LOADED;
                this.onLoaded(event);
            }
            else if (this._request.status > 0) {
                this._state = BaseLoader.STATE_ERROR;
                this.onError(event);
            }
        }
    }

    private onXHRProgress(event: any) {
        let newProgress: number;
        if (event.lengthComputable)
            newProgress = event.loaded / event.total;
        else
            newProgress = 1;

        let data =
        {
            progress: newProgress,
            progressChange: newProgress - this._lastProgress
        };

        this._lastProgress = newProgress;

        this.dispatchEvent(new LoaderEvent(LoaderEvent.PROGRESS, data));
    }
}
